import React, { useState } from 'react'
import type { Quiz, CreateQuizRequest } from '@/types/quiz'

interface QuizFormModalProps {
  lessonId: string
  quiz?: Quiz
  onClose: () => void
  onSave: (data: CreateQuizRequest) => Promise<void>
  isSaving?: boolean
}

export const QuizFormModal: React.FC<QuizFormModalProps> = ({ lessonId, quiz, onClose, onSave, isSaving: externalSaving }) => {
  const [formData, setFormData] = useState<Partial<CreateQuizRequest>>(
    quiz ? {
      title: quiz.title,
      description: quiz.description,
      passingScore: quiz.passingScore,
      timeLimitMinutes: quiz.timeLimitMinutes,
      maxAttempts: quiz.maxAttempts,
      lessonId: lessonId,
    } : {
      title: '',
      description: '',
      passingScore: 70,
      timeLimitMinutes: 30,
      maxAttempts: 3,
      lessonId: lessonId,
    }
  )

  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const isEditMode = !!quiz

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (!formData.title?.trim()) newErrors.title = 'Judul wajib diisi'
    if ((formData.passingScore || 0) < 0 || (formData.passingScore || 0) > 100) {
      newErrors.passingScore = 'Passing score harus 0-100'
    }
    if ((formData.timeLimitMinutes || 0) < 1) {
      newErrors.timeLimitMinutes = 'Durasi minimal 1 menit'
    }
    if ((formData.maxAttempts || 0) < 1) {
      newErrors.maxAttempts = 'Minimal 1 kali percobaan'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setIsSaving(true)
    try {
      await onSave({ ...formData, lessonId } as CreateQuizRequest)
    } catch (error) {
      console.error('Failed to save quiz:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const saving = externalSaving || isSaving

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-md transition-opacity" 
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-[0_32px_64px_-12px_rgba(0,0,0,0.2)] border border-slate-100 dark:border-white/5 overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-b border-slate-100 dark:border-white/5 bg-white/50 dark:bg-white/[0.02] backdrop-blur-xl">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <span className="material-symbols-outlined">{isEditMode ? 'quiz' : 'add_task'}</span>
            </div>
            <div>
              <h2 className="text-sm font-black text-slate-900 dark:text-white tracking-tight">
                {isEditMode ? 'Edit Quiz' : 'Buat Quiz Baru'}
              </h2>
              <p className="text-xs font-bold text-slate-400 mt-0.5">Atur parameter kuis untuk peserta</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="size-10 rounded-xl flex items-center justify-center hover:bg-slate-100 dark:hover:bg-white/5 text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <form id="quiz-form" onSubmit={handleSubmit} className="p-8 space-y-8">
            {/* Title */}
            <div className="space-y-1">
              <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                Judul Quiz <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                  setErrors((prev) => ({ ...prev, title: '' }))
                }}
                className={`w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 transition-all outline-none focus:ring-4 focus:ring-primary/10 ${
                  errors.title ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                }`}
                placeholder="Contoh: Kuis Evaluasi Modul 1"
              />
              {errors.title && <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.title}</p>}
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">Deskripsi Singkat</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 transition-all outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary resize-none"
                placeholder="Jelaskan secara singkat tujuan kuis ini..."
              />
            </div>

            {/* Config Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {/* Passing Score */}
              <div className="space-y-1">
                <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                  Passing Score <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.passingScore}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, passingScore: Number(e.target.value) }))
                      setErrors((prev) => ({ ...prev, passingScore: '' }))
                    }}
                    min="0"
                    max="100"
                    className={`w-full pl-5 pr-12 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white transition-all outline-none focus:ring-4 focus:ring-primary/10 ${
                      errors.passingScore ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                    }`}
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">%</span>
                </div>
                {errors.passingScore && <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.passingScore}</p>}
              </div>

              {/* Time Limit */}
              <div className="space-y-1">
                <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                  Durasi <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={formData.timeLimitMinutes}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, timeLimitMinutes: Number(e.target.value) }))
                      setErrors((prev) => ({ ...prev, timeLimitMinutes: '' }))
                    }}
                    min="1"
                    className={`w-full pl-5 pr-12 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white transition-all outline-none focus:ring-4 focus:ring-primary/10 ${
                      errors.timeLimitMinutes ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                    }`}
                  />
                  <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs font-black text-slate-400">menit</span>
                </div>
                {errors.timeLimitMinutes && <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.timeLimitMinutes}</p>}
              </div>

              {/* Max Attempts */}
              <div className="space-y-1 sm:col-span-2">
                <label className="block text-xs font-black text-slate-500 dark:text-slate-400 ml-1">
                  Maksimum Percobaan <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.maxAttempts}
                  onChange={(e) => {
                    setFormData((prev) => ({ ...prev, maxAttempts: Number(e.target.value) }))
                    setErrors((prev) => ({ ...prev, maxAttempts: '' }))
                  }}
                  min="1"
                  className={`w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-white/[0.03] border text-sm font-bold text-slate-900 dark:text-white transition-all outline-none focus:ring-4 focus:ring-primary/10 ${
                    errors.maxAttempts ? 'border-rose-500' : 'border-slate-200 dark:border-white/5 focus:border-primary'
                  }`}
                />
                <p className="mt-2 text-[10px] font-bold text-slate-400 ml-1">
                  Berapa kali siswa diizinkan mengulang kuis ini
                </p>
                {errors.maxAttempts && <p className="mt-2 text-xs font-bold text-rose-500 ml-1">{errors.maxAttempts}</p>}
              </div>
            </div>
          </form>
        </div>

        {/* Footer Actions */}
        <div className="flex-shrink-0 flex items-center justify-end gap-3 p-6 border-t border-slate-100 dark:border-white/5 bg-white/50 dark:bg-white/[0.02] backdrop-blur-xl">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-6 py-3 rounded-2xl text-xs font-black text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-white/5 transition-all disabled:opacity-50"
          >
            Batal
          </button>
          <button
            form="quiz-form"
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-primary text-white rounded-2xl font-black text-xs hover:opacity-90 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-xl shadow-primary/20"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-2 border-white/30 border-t-white" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-[18px]">{isEditMode ? 'check_circle' : 'add_circle'}</span>
                <span>{isEditMode ? 'Update Quiz' : 'Simpan Quiz'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

